import { ANCHOR_PROPERTY, discountedPrice } from '@js/utils/completeTheSet';

// RAZ-419: the line item property carrying each line's stock availability through to checkout, the order
// confirmation email, the order admin and analytics. No leading underscore, so Shopify shows it on all of
// those; the theme's own line items hide it instead (see visibleProperties), as the stock block shows it already.
export const STOCK_PROPERTY = 'Stock Availability';

// A cart is a handful of lines, so this only stops a misbehaving response from looping forever.
const MAX_STOCK_SYNCS = 50;

export default {
  name: 'cart',
  store() {
    return {
      // State
      data: null,
      loading: false,
      error: null,
      fetched: false,
      
      // Direct computed properties
      get items() { return this.data?.items || []; },
      get totalPrice() { return this.data?.total_price || 0; },
      get itemCount() { return this.data?.item_count || 0; },
      // Before any discounts, so the summary reads Subtotal − You save (total_discount) = Total. items_subtotal_price
      // already has line-level discounts (Complete the set, product discounts) taken off, which showed them twice.
      get subtotalPrice() { return this.data?.original_total_price || 0; },
      get totalDiscount() { return this.data?.total_discount || 0; },
      // RAZ-317: cart-level discount applications, emitted by cart.data.liquid.
      get discounts() { return this.data?.cart_discounts || []; },
      get currency() { return this.data?.currency || window.store_currency || 'USD'; },
      get note() { return this.data?.note || ''; },
      // RAZ-327: cart-level attributes, serialised by cart.data.liquid so the
      // vehicle stamp below can tell whether it has already been written.
      get attributes() { return this.data?.attributes || {}; },
      // RAZ-348: the mini cart's "Complete the set and save" rail, built by cart.data.liquid from what's in the cart.
      get completeTheSetProducts() { return this.data?.complete_the_set?.products || []; },
      get isEmpty() { return this.itemCount === 0; },
      
      // Initialize
      async init() {
        if (window.enableDebugMode) { console.log('stores/cart.js: Initializing Alpine store...'); }
        await this.fetch();
        this.setupEventListeners();
      },
      
      // Fetch cart data. A silent fetch (the background refresh when the tab regains focus) leaves
      // `loading` alone, so the cart icon, line items and buttons don't flash a loading state.
      async fetch({ isSilent = false } = {}) {
        if (window.enableDebugMode) { console.log('stores/cart.js: Fetching cart data, current loading state:', this.loading); }
        try {
          if (!isSilent) this.loading = true;
          this.error = null;
          
          let data = await this.request();

          // RAZ-419: correct any line whose stock property is missing or stale, however it reached the cart
          // (theme, app, rebuy, plain form post), then read the cart again so the UI holds the final keys.
          if (await this.syncStockProperties(data.items)) {
            data = await this.request();
          }

          this.data = data;
          
          // Dispatch event for other components
          window.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: this.data }
          }));
          
        } catch (error) {
          console.error('Cart fetch error:', error);
          this.error = error.message;
        } finally {
          if (!isSilent) this.loading = false;
          this.fetched = true;
          if (window.enableDebugMode) { console.log('stores/cart.js: Finished fetching cart data:', this.data); }
        }
      },
      
      // The cart as cart.data.liquid serialises it
      async request() {
        const response = await fetch('/cart?view=data');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      },

      // RAZ-419: write each line's stock availability (cart.data.liquid's `stock_availability`: the variant's
      // custom.variant_stock_availability, else the product's custom.product_stock_availability) into its
      // STOCK_PROPERTY, dropping the property when the metafield is blank. Lines are changed one at a time by
      // position, re-reading /cart/change.js each time, because a new property gives the line a new key.
      // Returns whether anything changed. A failure never blocks the cart; checkout just carries what's there.
      async syncStockProperties(items = []) {
        const stockByVariant = new Map(items.map((item) => [String(item.variant_id), item.stock_availability || '']));
        const isStale = (item) => (item.properties?.[STOCK_PROPERTY] || '') !== (stockByVariant.get(String(item.variant_id)) || '');

        let lines = items;
        let hasChanged = false;

        try {
          for (let attempt = 0; attempt < MAX_STOCK_SYNCS; attempt++) {
            const index = lines.findIndex(isStale);
            if (index === -1) break;

            const line = lines[index];
            const stock = stockByVariant.get(String(line.variant_id)) || '';
            const properties = { ...(line.properties || {}) };
            if (stock) {
              properties[STOCK_PROPERTY] = stock;
            } else {
              delete properties[STOCK_PROPERTY];
            }

            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ line: index + 1, quantity: line.quantity, properties })
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            lines = (await response.json()).items || [];
            hasChanged = true;
          }
        } catch (error) {
          if (window.enableDebugMode) { console.warn('stores/cart.js: stock availability property not synced', error); }
        }

        return hasChanged;
      },

      // A line's properties for the theme's line items: no private underscore keys, no blanks, and not the
      // stock property, which the line item stock block already shows.
      visibleProperties(item) {
        return Object.entries(item?.properties || {})
          .filter(([key, value]) => key && value && !key.startsWith('_') && key !== STOCK_PROPERTY);
      },

      // Head to checkout from the mini cart or the cart page's summary. Re-reads the cart first (which syncs the stock
      // property) to catch anything an app added without telling the store. `loading` stays on while the
      // browser leaves, so the button can't be pressed twice.
      async checkout() {
        if (this.isEmpty || this.loading) return;

        this.loading = true;
        await this.fetch({ isSilent: true });
        window.location.href = '/checkout';
      },

      // Add one item to cart
      async add(variantId, quantity = 1, properties = {}) {
        return this.addItems([{ id: variantId, quantity, properties }]);
      },

      // Add several items in one request, e.g. the main product plus its ticked Complete the Set rows.
      // `cart:item_added` fires once; its variantId, quantity and properties describe the first item.
      async addItems(items) {
        try {
          this.loading = true;

          // RAZ-327: every add-to-cart action funnels through here, so the saved
          // vehicle is captured once, in one place, on every line in the request.
          const lineItems = items.map((item) => ({
            ...item,
            properties: this.withVehicleProperty(item.properties)
          }));

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: lineItems })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.description || error.message || 'Failed to add item');
          }

          await this.captureVehicleAttribute();
          await this.fetch(); // Refresh cart data

          const [{ id: variantId, quantity, properties }] = items;
          window.dispatchEvent(new CustomEvent('cart:item_added', {
            detail: { variantId, quantity, properties, items }
          }));

          return true;
        } catch (error) {
          console.error('Add to cart error:', error);
          this.error = error.message;
          throw error;
        } finally {
          this.loading = false;
        }
      },
      
      // Update item quantity
      async updateQuantity(key, quantity) {
        try {
          this.loading = true;
          
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity })
          });
          
          if (!response.ok) throw new Error('Failed to update quantity');
          
          await this.fetch();
          
          window.dispatchEvent(new CustomEvent('cart:quantity_updated', {
            detail: { key, quantity }
          }));
          
          return true;
        } catch (error) {
          console.error('Update quantity error:', error);
          this.error = error.message;
          throw error;
        } finally {
          this.loading = false;
        }
      },
      
      // Update UI elements directly for immediate feedback
      updateItemUI(key, quantity) {
        const itemElement = document.querySelector(`[data-item-key="${key}"]`);
        if (!itemElement) return;
        
        // Update quantity input
        const quantityInput = itemElement.querySelector('[data-quantity-input]');
        if (quantityInput) {
          quantityInput.value = quantity;
        }
        
        // Hide item if quantity is 0
        const itemWrapper = itemElement.closest('.line-item-wrapper');
        if (itemWrapper && quantity <= 0) {
          itemWrapper.style.display = 'none';
        }
        
        // Update prices if we have the updated item data
        const updatedItem = this.getItemByKey(key);
        if (updatedItem) {
          const priceElement = itemWrapper?.querySelector('[data-final-price]');
          if (priceElement) {
            priceElement.textContent = this.formatPrice(updatedItem.final_line_price);
          }
        }
      },
      
      // Remove item from cart
      async remove(key) {
        return this.updateQuantity(key, 0);
      },
      
      // Update cart note
      async updateNote(note) {
        try {
          this.loading = true;
          
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note })
          });
          
          if (!response.ok) throw new Error('Failed to update note');
          
          this.data = await response.json();
          return true;
        } catch (error) {
          console.error('Update note error:', error);
          this.error = error.message;
          throw error;
        } finally {
          this.loading = false;
        }
      },
      
      // RAZ-327: the saved vehicle as a sentence, or '' when there is nothing to
      // capture or the merchant has switched capture off.
      get vehicleSentence() {
        if (!window.themeSettings?.vehicleCaptureOnCart) return '';
        const store = window.Alpine?.store('vehicle');
        if (!store?.hasVehicle) return '';
        return store.sentence || '';
      },

      // Merge the _Vehicle line item property in. The leading underscore keeps it
      // hidden in the cart UI while it still reaches the order.
      withVehicleProperty(properties = {}) {
        const sentence = this.vehicleSentence;
        if (!sentence) return properties;
        return { ...properties, _Vehicle: sentence };
      },

      // Stamp the vehicle on the cart itself, once per cart, so the whole order
      // carries it rather than just the lines added after the vehicle was saved.
      async captureVehicleAttribute() {
        const sentence = this.vehicleSentence;
        if (!sentence || this.attributes.Vehicle === sentence) return;

        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { Vehicle: sentence } })
          });
        } catch (error) {
          // A failed attribute write must never block the add to cart.
          if (window.enableDebugMode) { console.warn('stores/cart.js: vehicle attribute not set', error); }
        }
      },

      // Helper methods
      getItem(variantId) {
        return this.items.find(item => item.variant_id == variantId);
      },
      
      getItemByKey(key) {
        return this.items.find(item => item.key === key);
      },
      
      hasItem(variantId) {
        return !!this.getItem(variantId);
      },
      
      formatPrice(price) {
        const symbol = window.store_currency_symbol || '$';
        return `${symbol}${(price / 100).toFixed(2)}`;
      },

      // A price in cents after the Complete the set percentage (unchanged when the percentage is 0)
      discounted(cents) {
        return discountedPrice(cents);
      },

      // Adds one Complete the Set item anchored to the product it completes, so the discount applies.
      async addCompleteTheSetItem(variantId, anchorProductId) {
        return this.addItems([{ id: variantId, quantity: 1, properties: { [ANCHOR_PROPERTY]: String(anchorProductId) } }]);
      },
      
      // Event listeners
      setupEventListeners() {
        // Refresh cart on focus (other tabs/checkout completion)
        let refreshTimeout;
        window.addEventListener('focus', () => {
          clearTimeout(refreshTimeout);
          refreshTimeout = setTimeout(() => this.fetch({ isSilent: true }), 1000);
        });
        
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) {
            clearTimeout(refreshTimeout);
            refreshTimeout = setTimeout(() => this.fetch({ isSilent: true }), 1000);
          }
        });
      }
    };
  }
};